import { Box, Typography, Paper, Grid } from "@mui/material";
import {
  MonetizationOn,
  CheckCircleOutline,
  HourglassEmpty,
  AccountBalanceWallet,
} from "@mui/icons-material";
import type { ReactNode } from "react";

interface StatCardData {
  title: string;
  value: string;
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  selected?: boolean;
}

const StatCard = ({ stat }: { stat: StatCardData }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 4,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      height: "100%",
      outline: stat.selected ? `2px solid #3b82f6` : "none",
      outlineOffset: stat.selected ? -2 : 0,
    }}
  >
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {stat.title}
      </Typography>
      <Typography variant="h4" fontWeight="bold">
        {stat.value}
      </Typography>
    </Box>
    <Box
      sx={{
        bgcolor: stat.iconBg,
        p: 1.5,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {stat.icon}
    </Box>
  </Paper>
);

export interface CampaignStats {
  totalPaid: number;
  totalPending: number;
  amountRaised: number;
  totalWithdrawn: number;
  currency: string;
}

const fmt = (amount: number, currency: string) => {
  const prefix = currency === "ZWL" ? "ZWL " : "$";
  return `${prefix}${amount.toFixed(2)}`;
};

const CampaignStatsCards = ({ stats }: { stats: CampaignStats }) => {
  const cards: StatCardData[] = [
    {
      title: "Total CHEMA Paid",
      value: fmt(stats.totalPaid, stats.currency),
      icon: <MonetizationOn sx={{ color: "#10b981" }} />,
      iconBg: "#ecfdf5",
      iconColor: "#10b981",
    },
    {
      title: "CHEMA Received",
      value: fmt(stats.amountRaised, stats.currency),
      icon: <CheckCircleOutline sx={{ color: "#f59e0b" }} />,
      iconBg: "#fffbeb",
      iconColor: "#f59e0b",
    },
    {
      title: "CHEMA Pending",
      value: fmt(stats.totalPending, stats.currency),
      icon: <HourglassEmpty sx={{ color: "#f59e0b" }} />,
      iconBg: "#fffbeb",
      iconColor: "#f59e0b",
    },
    {
      title: "Total CHEMA Withdrawn",
      value: fmt(stats.totalWithdrawn, stats.currency),
      icon: <AccountBalanceWallet sx={{ color: "#9ca3af" }} />,
      iconBg: "#f3f4f6",
      iconColor: "#9ca3af",
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {cards.map((card) => (
        <Grid key={card.title} size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard stat={card} />
        </Grid>
      ))}
    </Grid>
  );
};

export default CampaignStatsCards;
